import { RefObject } from 'react';
import { useEffect, useState } from 'react';
import { debounce } from '@/utils/debounce';

interface Props {
  containerRef: RefObject<HTMLElement>;
  step: number;
  threshold: number;
  gap: number;
}

export function useScroller(
  { containerRef, step, threshold, gap }: Props,
  dependencies: unknown[]
) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const handleScrollerClick = () => {
    const container = containerRef?.current;

    if (!container) return;

    const scrollLeft = container.scrollLeft + step;
    const maxScrollLeft = container.scrollWidth - container.clientWidth;

    if (!isScrolled) {
      if (scrollLeft < maxScrollLeft) {
        const isEnd = scrollLeft > maxScrollLeft - threshold;

        if (isEnd) {
          container.scrollBy({ left: maxScrollLeft, behavior: 'smooth' });
          setIsScrolled(true);
        } else {
          container.scrollBy({ left: step, behavior: 'smooth' });
        }
      } else {
        container.scrollBy({
          behavior: 'smooth',
          left: maxScrollLeft - container.scrollLeft,
        });
        setIsScrolled(true);
      }

      return;
    }

    if (container.scrollLeft - step > threshold) {
      container.scrollBy({ left: -step, behavior: 'smooth' });
    } else {
      container.scrollBy({ left: -container.scrollLeft, behavior: 'smooth' });
      setIsScrolled(false);
    }
  };

  useEffect(() => {
    const container = containerRef.current;

    if (!container) return;

    const handleScroll = () => {
      if (
        container.scrollLeft + gap >=
        container.scrollWidth - container.clientWidth
      ) {
        container.scrollBy({ left: container.scrollLeft });
        setIsScrolled(true);
        return;
      }

      if (container.scrollLeft - gap <= 0) {
        container.scrollBy({ left: 0 });
        setIsScrolled(false);
      }
    };

    const checkOverflow = () => {
      const overflowPx = container.scrollWidth - container.clientWidth;
      setIsOverflowing(overflowPx > threshold);
    };

    checkOverflow();

    const resizeObserver = new ResizeObserver(checkOverflow);

    resizeObserver.observe(container);
    container.addEventListener('scroll', debounce(handleScroll, 100));

    return () => {
      resizeObserver.disconnect();
      container.removeEventListener('scroll', debounce(handleScroll, 100));
    };
  }, dependencies);

  return { isOverflowing, isScrolled, onScrollerClick: handleScrollerClick };
}
